import React, { useState } from 'react';
import { useTranslation } from '@/store/languageStore';
import apiClient from '@/services/api/client';
import {
  VehicleAttachmentCategoryField,
  type CustomAttachmentCategory,
} from './VehicleAttachmentCategoryField';

interface VehicleAttachmentEditFormProps {
  vehicleId: number;
  attachmentId: number;
  category: string;
  description: string | null;
  categories: CustomAttachmentCategory[];
  onCreated: (category: CustomAttachmentCategory) => void;
  onSaved: (updated: {
    id: number;
    category: string;
    category_name?: string | null;
    description: string | null;
  }) => void;
  onCancel: () => void;
}

export const VehicleAttachmentEditForm: React.FC<VehicleAttachmentEditFormProps> = ({
  vehicleId,
  attachmentId,
  category,
  description,
  categories,
  onCreated,
  onSaved,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [editCategory, setEditCategory] = useState(category);
  const [editDescription, setEditDescription] = useState(description ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    try {
      setSaving(true);
      setError(null);
      const response = await apiClient.put(
        `/api/vehicles/${vehicleId}/attachments/${attachmentId}`,
        { category: editCategory, description: editDescription }
      );
      if (response.data?.success && response.data.data) {
        onSaved(response.data.data);
      } else {
        setError(response.data?.error || t('vehicleAttachments.saveFailed'));
      }
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(message || t('vehicleAttachments.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t border-border bg-blue-50/70 p-3 space-y-3" onClick={(e) => e.stopPropagation()}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <VehicleAttachmentCategoryField
          vehicleId={vehicleId}
          value={editCategory}
          categories={categories}
          onChange={setEditCategory}
          onCreated={onCreated}
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('vehicleAttachments.description')}
          </label>
          <input
            type="text"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            placeholder={t('vehicleAttachments.descriptionPlaceholder')}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-4 py-2 text-textDefault border border-border rounded-lg bg-bgLight hover:bg-bgElevated"
        >
          {t('common.cancel')}
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
        >
          {t('common.save')}
        </button>
      </div>
    </div>
  );
};
